'use client';

/**
 * ReferredClientsTable - Table matching target dark theme design
 * Transparent backgrounds, subtle borders, precise spacing
 */

import React, { useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ReferredClient } from '../types';
import { cn } from '@/lib/utils';
import { SUBSCRIPTION_PLANS } from '@/features/subscriptions/lib/stripe/plans';
import { formatPrice } from '@/lib/utils/format-price';
import {
  ReferredClientsFilters,
  type StatusFilter,
  type SortField,
  type SortOrder,
} from './ReferredClientsFilters';

interface ReferredClientsTableProps {
  clients: ReferredClient[];
}

// Status badge styles
const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  active: {
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-600 dark:text-emerald-400',
  },
  payment_pending: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-600 dark:text-amber-400',
  },
  past_due: {
    bg: 'bg-red-500/10',
    text: 'text-red-600 dark:text-red-400',
  },
  canceled: {
    bg: 'bg-zinc-100 dark:bg-zinc-800/80',
    text: 'text-zinc-500',
  },
  inactive: {
    bg: 'bg-zinc-100 dark:bg-zinc-800/80',
    text: 'text-zinc-500',
  },
};

const STATUS_LABELS: Record<string, string> = {
  active: 'ACTIVE',
  payment_pending: 'TRIAL',
  past_due: 'PAST DUE',
  canceled: 'CANCELED',
  inactive: 'INACTIVE',
};

const formatPlanPrice = (planLevel: ReferredClient['planLevel']) => {
  const plan = SUBSCRIPTION_PLANS[planLevel] || SUBSCRIPTION_PLANS.basic;
  const price = Number(plan.price.toFixed(2));
  return `€${price}/mo`; // @format-helper-currency-glyph-ok reason=CMG-platform-subscription-pricing-is-EUR
};

const STATUS_RANK: Record<string, number> = {
  active: 0,
  payment_pending: 1,
  past_due: 2,
  canceled: 3,
  inactive: 4,
};

export function ReferredClientsTable({ clients }: ReferredClientsTableProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('status');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Live count per status (across the full period-scoped dataset) for the chips.
  const counts = useMemo(() => {
    const acc: Record<StatusFilter, number> = {
      all: clients.length,
      active: 0,
      payment_pending: 0,
      past_due: 0,
      canceled: 0,
      inactive: 0,
    };
    for (const c of clients) acc[c.status] = (acc[c.status] ?? 0) + 1;
    return acc;
  }, [clients]);

  // Filter by status, then sort. Comparisons are "natural ascending"; the
  // direction toggle flips the whole order so behavior is always predictable.
  const visibleClients = useMemo(() => {
    const filtered =
      statusFilter === 'all' ? clients : clients.filter((c) => c.status === statusFilter);

    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'status':
          comparison = (STATUS_RANK[a.status] ?? 5) - (STATUS_RANK[b.status] ?? 5);
          break;
        case 'clientName':
          comparison = a.clientName.localeCompare(b.clientName);
          break;
        case 'planLevel': {
          const planOrder = { enterprise: 0, pro: 1, basic: 2 };
          comparison = planOrder[a.planLevel] - planOrder[b.planLevel];
          break;
        }
        case 'commission':
          comparison = a.monthlyCommission - b.monthlyCommission;
          break;
        case 'totalEarned':
          comparison = a.totalEarnedCommission - b.totalEarnedCommission;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [clients, statusFilter, sortField, sortOrder]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-3">
        <div>
          <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">
            Referred Clients List
          </h3>
          <p className="text-xs text-zinc-600 mt-1">
            Track your referrals and maximize earnings with insights.
          </p>
        </div>
        {clients.length > 0 && (
          <ReferredClientsFilters
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            sortField={sortField}
            onSortFieldChange={setSortField}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
            counts={counts}
          />
        )}
      </div>

      {/* Table - Bubble Style */}
      <div className="rounded-[2rem] bg-white/60 dark:bg-zinc-900/40 backdrop-blur-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
        {clients.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Building2 className="h-10 w-10 mx-auto text-zinc-400 dark:text-zinc-700 mb-4" />
            <p className="text-sm text-zinc-500 dark:text-zinc-600">
              No referred clients yet. Share your partner code to start earning!
            </p>
          </div>
        ) : (
          <Table className="w-full">
            <TableHeader>
              <TableRow className="border-b border-zinc-200 dark:border-zinc-800 hover:bg-transparent">
                <TableHead
                  className="text-[10px] uppercase tracking-widest text-zinc-600 font-medium py-4 px-6"
                  style={{ width: '120px' }}
                >
                  Status
                </TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-zinc-600 font-medium py-4 px-6">
                  Client Name
                </TableHead>
                <TableHead
                  className="text-[10px] uppercase tracking-widest text-zinc-600 font-medium py-4 px-6"
                  style={{ width: '140px' }}
                >
                  Description
                </TableHead>
                <TableHead
                  className="text-[10px] uppercase tracking-widest text-zinc-600 font-medium py-4 px-6 text-right"
                  style={{ width: '120px' }}
                >
                  Monthly
                </TableHead>
                <TableHead
                  className="text-[10px] uppercase tracking-widest text-zinc-600 font-medium py-4 px-6 text-right"
                  style={{ width: '120px' }}
                >
                  Total Earned
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {visibleClients.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={5} className="text-center py-12">
                    <p className="text-sm text-zinc-500 dark:text-zinc-600">
                      No clients match this filter.
                    </p>
                  </TableCell>
                </TableRow>
              )}
              {visibleClients.map((client) => {
                const statusStyle = STATUS_STYLES[client.status] || STATUS_STYLES.inactive;

                return (
                  <TableRow
                    key={client.id}
                    className="border-b border-zinc-100 dark:border-zinc-800/50 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors"
                  >
                    <TableCell className="py-4 px-6">
                      <span
                        className={cn(
                          'inline-block px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wide',
                          statusStyle.bg,
                          statusStyle.text
                        )}
                      >
                        {STATUS_LABELS[client.status]}
                      </span>
                    </TableCell>

                    <TableCell className="py-4 px-6">
                      <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                        {client.clientName}
                      </p>
                      {client.clientEmail && (
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                          {client.clientEmail}
                        </p>
                      )}
                    </TableCell>

                    <TableCell className="py-4 px-6">
                      <span className="text-sm text-zinc-500">
                        {formatPlanPrice(client.planLevel)}
                      </span>
                      <p className="text-xs text-zinc-500 mt-1">
                        Started {formatDate(client.billingStartedAt)}
                      </p>
                      {client.status === 'payment_pending' && (
                        <p className="text-xs text-amber-500 mt-1">
                          {client.trialEndsAt
                            ? `Trial until ${formatDate(client.trialEndsAt)}`
                            : 'Trial active'}
                        </p>
                      )}
                      {client.status === 'past_due' && (
                        <p className="text-xs text-red-500 mt-1">
                          {client.cancelAt
                            ? `Cancels ${formatDate(client.cancelAt)}`
                            : 'Payment failed'}
                        </p>
                      )}
                      {client.status === 'canceled' && (
                        <p className="text-xs text-zinc-500 mt-1">Subscription ended</p>
                      )}
                    </TableCell>

                    <TableCell className="py-4 px-6 text-right">
                      <span className="text-sm font-black text-emerald-500">
                        {formatPrice(client.monthlyCommission, 'EUR', { minimumFractionDigits: 2 })}
                      </span>
                    </TableCell>

                    <TableCell className="py-4 px-6 text-right">
                      <span className="text-sm font-medium text-zinc-400">
                        {formatPrice(client.totalEarnedCommission, 'EUR', {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
